<template>
  <table class="table b-table table-striped table-hover" v-if="relatedAccountData">
    <thead>
    <tr>
      <th
        @click="sortBy('user_id')"
        :aria-sort="sortKey === 'user_id' ? sortAsc ? 'ascending': 'descending': 'none'"
      >
        User
      </th>
      <th
        @click="sortBy('app_name')"
        :aria-sort="sortKey === 'app_name' ? sortAsc ? 'ascending': 'descending': 'none'"
      >
        App
      </th>
      <th
        @click="sortBy('completes_count')"
        :aria-sort="sortKey === 'completes_count' ? sortAsc ? 'ascending': 'descending': 'none'"
      >
        Completes
      </th>
      <th
        @click="sortBy('publisher_payout_sum')"
        :aria-sort="sortKey === 'publisher_payout_sum' ? sortAsc ? 'ascending': 'descending': 'none'"
      >
        Pub. Payout
      </th>
      <th
        @click="sortBy('iap_data')"
        :aria-sort="sortKey === 'iap_data' ? sortAsc ? 'ascending': 'descending': 'none'"
      >
        IAP Data
      </th>
      <th
        @click="sortBy('user_created_date')"
        :aria-sort="sortKey === 'user_created_date' ? sortAsc ? 'ascending': 'descending': 'none'"
      >
        User Created
      </th>
      <th
        @click="sortBy('matching_attributes')"
        :aria-sort="sortKey === 'matching_attributes' ? sortAsc ? 'ascending': 'descending': 'none'"
      >
        Matching Attributes
      </th>
      <th
        @click="sortBy('matching_probability')"
        :aria-sort="sortKey === 'matching_probability' ? sortAsc ? 'ascending': 'descending': 'none'"
      >
        Matching Probability
      </th>
      <th style="width: 80px" title="Pinned connections persist past dynamic-match thresholds">
        Pin
      </th>
    </tr>
    </thead>
    <tbody>
    <tr v-for="relatedAccount in sortedRelatedAccountData" :key="relatedAccount.id">
      <td>
        <UserElement :id="relatedAccount.user_id" :status="relatedAccount.status"/>
      </td>
      <td>
        <AppWithLogo :appName="relatedAccount.app_name" />
      </td>
      <td>{{ relatedAccount.completes_count | formatCount }}</td>
      <td>{{ relatedAccount.publisher_payout_sum | newFormatCurrencyDecimals }}</td>
      <td>{{ relatedAccount.iap_data | newFormatCurrencyDecimals }}</td>
      <td>{{ relatedAccount.user_created_date | dotDateShort }}</td>
      <td>{{ relatedAccount.formatted_matching_attributes }}</td>
      <td :class="getProbabilityClass(relatedAccount.matching_probability)">
        {{ relatedAccount.matching_probability }} %
      </td>
      <td class="text-center">
        <BButton
          variant="link"
          size="sm"
          :class="{'text-warning': relatedAccount.is_pinned}"
          :disabled="pendingIds.includes(relatedAccount.user_id)"
          v-b-tooltip.hover.top="relatedAccount.is_pinned ? 'Unpin' : 'Pin'"
          @click="togglePin(relatedAccount)"
        >
          <FeatherIcon
            icon="MapPinIcon"
            :fill="relatedAccount.is_pinned ? 'currentColor' : 'none'"
            size="18"
          />
        </BButton>
      </td>
    </tr>
    </tbody>
  </table>
</template>

<script>
import probabilityMixin from '@/views/users/mixins/probabilityMixin'
import AppWithLogo from '@/views/users/UserDetails/AppWithLogo.vue'
import { BButton, VBTooltip } from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  components: {AppWithLogo, BButton, FeatherIcon},
  directives: { 'b-tooltip': VBTooltip },
  mixins: [probabilityMixin],
  props: {
    relatedAccountData: {
      type: Array,
      default: () => {
        return []
      }
    },
    userId: {
      type: [Number, String],
      required: true
    }
  },
  data () {
    return {
      sortKey: null, // default sort column
      sortAsc: true,
      pendingIds: []
    }
  },
  computed: {
    sortedRelatedAccountData () {
      if (!this.sortKey) {
        return [...this.relatedAccountData].sort(
          (a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0)
        )
      }

      return [...this.relatedAccountData].sort((a, b) => {
        let valA = a[this.sortKey]
        let valB = b[this.sortKey]

        // Fallback for strings: case-insensitive
        if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase()
          valB = valB.toLowerCase()
        }

        if (valA < valB) return this.sortAsc ? -1 : 1
        if (valA > valB) return this.sortAsc ? 1 : -1
        return 0
      })
    }
  },
  methods: {
    sortBy (key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc
      } else {
        this.sortKey = key
        this.sortAsc = true
      }
    },
    async togglePin (account) {
      if (this.pendingIds.includes(account.user_id)) return
      this.pendingIds.push(account.user_id)
      try {
        if (account.is_pinned) {
          await this.$http.delete(`/api/user/${this.userId}/related-accounts/pin/${account.user_id}`)
          this.$emit('unpinned', { connectedUserId: account.user_id })
          this.alertSuccess('Connected account unpinned')
        } else {
          await this.$http.post(`/api/user/${this.userId}/related-accounts/pin`, {
            connected_user_id: account.user_id
          })
          this.$emit('pinned', { connectedUserId: account.user_id })
          this.alertSuccess('Connected account pinned')
        }
      } catch (errors) {
        if (errors && errors.response && errors.response.data) {
          this.alertError(errors.response.data.errors)
        }
      } finally {
        this.pendingIds = this.pendingIds.filter(id => id !== account.user_id)
      }
    }
  }
}
</script>
